import {Component} from '@angular/core';
import {DatePipe, NgForOf, NgIf} from "@angular/common";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {Router, RouterLink} from "@angular/router";
import {Teacher} from "../interfaces/teacher";
import {Event} from "../interfaces/event";
import {TeacherService} from "../services/teacher.service";
import {EventService} from "../services/event.service";

@Component({
  selector: 'app-events',
  templateUrl: './events.component.html',
  styleUrl: './events.component.css',
  standalone: true,
  imports: [
    DatePipe,
    NgForOf,
    NgIf,
    ReactiveFormsModule,
    RouterLink,
    FormsModule
  ]
})
export class EventsComponent {
  constructor(private router: Router, private eventService: EventService, private teacherService: TeacherService) {
  };

  eventProfile: Event[] = [];
  events: Event[] = [];
  searchValue: string = '';
  teacher: Teacher | null = null;
  filteredEvents: Event[] = [];

  // PAGINATION
  cardsPerPage = 10;
  currentPage = 1;
  totalPages = 0;
  pages: number[] = [];

  async ngOnInit() {
    this.filteredEvents = await this.eventService.getEvents();
    this.eventProfile = [...this.filteredEvents];
    this.teacher = await this.teacherService.getTeacher();
  }

  updatePagination() {
    this.totalPages = Math.ceil(this.eventProfile.length / this.cardsPerPage);
    this.pages = Array.from({length: this.totalPages}, (_, i) => i + 1);
  }

  get pagedCards() {
    const start = (this.currentPage - 1) * this.cardsPerPage;
    return this.filteredEvents.slice(start, start + this.cardsPerPage);
  }

  goToPage(page: number) {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
  }

  goToPrevious() {
    if (this.currentPage > 1) this.currentPage--;
  }

  goToNext() {
    if (this.currentPage < this.totalPages) this.currentPage++;
  }

  selectChange(event: any) {
    const numberSelected = event.target as HTMLSelectElement;
    if (numberSelected.value !== "All") {
      this.cardsPerPage = Number(numberSelected.value);
    } else {
      this.cardsPerPage = this.eventProfile.length;
    }
    this.updatePagination();
  }

  sorts: { field: string; dir: 'asc' | 'desc' }[] = [];

  sort(field: string) {
    const current = this.sorts[0];

    if (current?.field === field) {
      current.dir = current.dir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sorts = [{field, dir: 'asc'}];
    }
    this.applySorting();
  }

  isEmpty(val: any): boolean {
    return val === null ||
      val === undefined ||
      val === '' ||
      !String(val).trim();
  }

  selectedStatusSort: string | null = null;
  eventStatusOrder = ['active', 'expired', 'cancelled'];

  onStatusSortChange(event: any) {
    const select = event.target as HTMLSelectElement;
    this.selectedStatusSort = select.value || null;

    this.sorts = [{field: 'status', dir: 'asc'}];
    this.applySorting();
  }

  applySorting() {
    this.filteredEvents.sort((a, b) => {
      for (const s of this.sorts) {
        let aVal: any;
        let bVal: any;
        let result = 0;

        switch (s.field) {
          case 'name':
            aVal = a.name?.trim().toLowerCase();
            bVal = b.name?.trim().toLowerCase();
            break;

          case 'link':
            aVal = a.webLink?.trim().toLowerCase();
            bVal = b.webLink?.trim().toLowerCase();
            break;

          case 'start':
            aVal = new Date(a.startDate ?? 0).getTime();
            bVal = new Date(b.startDate ?? 0).getTime();
            break;

          case 'end':
            aVal = new Date(a.endDate ?? 0).getTime();
            bVal = new Date(b.endDate ?? 0).getTime();
            break;

          case 'status':
            const selected = this.selectedStatusSort;

            aVal = a.status;
            bVal = b.status;

            if (selected) {
              if (aVal === selected && bVal !== selected) return -1;
              if (aVal !== selected && bVal === selected) return 1;
            }
            result =
              this.rank(aVal, this.eventStatusOrder) -
              this.rank(bVal, this.eventStatusOrder);
            break;
        }

        const aEmpty = this.isEmpty(aVal);
        const bEmpty = this.isEmpty(bVal);

        if (aEmpty && !bEmpty) return 1;
        if (!aEmpty && bEmpty) return -1;
        if (aEmpty && bEmpty) continue;

        if (typeof aVal === 'string') {
          result = aVal.localeCompare(bVal);
        } else {
          result = aVal - bVal;
        }

        if (result !== 0) {
          return s.dir === 'asc' ? result : -result;
        }
      }
      return 0;
    });
    this.currentPage = 1;

    this.updatePagination();
  }

  rank(value: string, order: string[]): number {
    const i = order.indexOf(value);
    return i === -1 ? order.length : i;
  }

  getSortDir(field: string): 'asc' | 'desc' | null {
    const s = this.sorts.find(sort => sort.field === field);
    return s ? s.dir : null;
  }

  // FILTERED SEARCH
  filter(search: string) {
    const query = search.toLowerCase().trim();

    if (!query) {
      this.filteredEvents = [...this.eventProfile];
    } else {
      this.filteredEvents = this.eventProfile.filter(event =>
        (event.status?.toLowerCase() ?? '').includes(query) ||
        (event.name?.toLowerCase() ?? '').includes(query) ||
        (event.webLink?.toLowerCase() ?? '').includes(query) ||
        event.startDate?.toString().includes(query) ||
        event.startDate?.toString().includes(query)
      );
    }
    this.currentPage = 1;
    this.updatePagination();
  }

  onEventClicked(event: Event) {
    this.router.navigate([`/events/${event.id}`]);
  }

  showEvents(event: any) {
    const selection = event.target as HTMLSelectElement;
    const value = selection.value;

    if (value === 'All Events') {
      this.filteredEvents = [...this.eventProfile];
    } else if (value === 'Undeleted') {
      this.filteredEvents = this.eventProfile.filter(e => !e.isDeleted);
    } else if (value === 'Deleted') {
      this.filteredEvents = this.eventProfile.filter(e => e.isDeleted);
    }

    this.currentPage = 1;
    this.updatePagination();
  }
}
